import { useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DOMPurify from "dompurify";

import getEndpoints from "lib/apiEndpoints";
import styles, { KARSTEN, TRUSSARDI } from "./styles";

const useStyles = makeStyles(styles);

const API_KEY = "4loZGJ5Nw76xEvh7bpN8941ji26JpbqN6sfPtag1";
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Alexandria:wght@400;500;600;700&display=swap";

function anchorId(versao) {
  return `v${versao}`;
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Página pública (sem autenticação, fora do layout /admin) — não usa o helper Api padrão porque
// ele sempre monta headers de sessão (k-iduser/Bearer) e faz refresh de token; aqui não existe
// usuário logado. Chamada direta contra a rota [AllowAnonymous] GET public/list.
async function fetchNotasPublicas() {
  const endpoints = getEndpoints();
  const response = await fetch(
    `${endpoints.releaseNotesDigitalApi}public/list`,
    {
      method: "GET",
      headers: { "x-api-key": API_KEY },
    },
  );
  const data = await response.json();
  if (!response.ok || data?.Success === false) {
    throw new Error(
      data?.ValidationMessage ||
        data?.Message ||
        "Não foi possível carregar as notas de atualização.",
    );
  }
  return data.Result || [];
}

function ensureAlexandriaFont() {
  if (document.getElementById("knt-alexandria-font")) return;
  const link = document.createElement("link");
  link.id = "knt-alexandria-font";
  link.rel = "stylesheet";
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

function KonnectMark({ size = 36 }) {
  const needle = KARSTEN;
  const stitch = TRUSSARDI;
  const cx = size / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden
    >
      <line
        x1={cx}
        y1={size * 0.08}
        x2={cx}
        y2={size * 0.46}
        stroke={needle}
        strokeWidth={size * 0.05}
        strokeLinecap="round"
      />
      <ellipse
        cx={cx}
        cy={size * 0.18}
        rx={size * 0.03}
        ry={size * 0.08}
        fill="transparent"
        stroke={needle}
        strokeWidth={size * 0.025}
      />
      <path
        d={`M${cx - size * 0.42} ${size * 0.76} Q${cx - size * 0.18} ${size * 0.62} ${cx} ${size * 0.76} T${cx + size * 0.42} ${size * 0.76}`}
        fill="none"
        stroke={stitch}
        strokeWidth={size * 0.045}
        strokeLinecap="round"
        strokeDasharray={`${size * 0.14} ${size * 0.1}`}
      />
    </svg>
  );
}

function BrandHeader({ classes, total, notas, activeId }) {
  return (
    <header className={classes.header}>
      <div className={classes.headerInner}>
        <div className={classes.logoRow}>
          <KonnectMark />
          <div>
            <p className={classes.brandWordmark}>
              konnect<span className={classes.brandPlus}>+</span>
            </p>
            <span className={classes.brandSignature}>KARSTEN · TRUSSARDI</span>
          </div>
        </div>
        <div className={classes.accentBar} />
        <div className={classes.headerCopy}>
          <h1 className={classes.headerTitle}>Notas de atualização</h1>
          <p className={classes.headerSubtitle}>
            {total > 0
              ? `Todas as novidades do app, da mais recente para a mais antiga · ${total} vers${total === 1 ? "ão" : "ões"}`
              : "Todas as novidades do app konnect+"}
          </p>
        </div>
      </div>
      {notas?.length > 0 && (
        <VersionNav
          classes={classes}
          notas={notas}
          activeId={activeId}
          variant="mobile"
        />
      )}
    </header>
  );
}

function VersionNav({ classes, notas, activeId, variant }) {
  if (!notas.length) return null;

  if (variant === "mobile") {
    return (
      <nav className={classes.mobileNav} aria-label="Versões">
        {notas.map((nota) => {
          const id = anchorId(nota.Versao);
          const active = activeId === id;
          return (
            <a
              key={nota.Versao}
              href={`#${id}`}
              className={`${classes.chip}${active ? ` ${classes.chipActive}` : ""}`}
            >
              v{nota.Versao}
            </a>
          );
        })}
      </nav>
    );
  }

  return (
    <aside className={classes.sidebar} aria-label="Índice de versões">
      <p className={classes.sidebarLabel}>Versões</p>
      <ul className={classes.sidebarList}>
        {notas.map((nota) => {
          const id = anchorId(nota.Versao);
          const active = activeId === id;
          return (
            <li key={nota.Versao}>
              <a
                href={`#${id}`}
                className={`${classes.sidebarLink}${active ? ` ${classes.sidebarLinkActive}` : ""}`}
              >
                v{nota.Versao}
              </a>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function ReleaseArticle({ classes, nota, isLatest }) {
  const id = anchorId(nota.Versao);
  const dataLabel = formatDate(nota.DataPublicacao);
  const html = DOMPurify.sanitize(nota.ConteudoHtml || "");

  return (
    <article id={id} className={classes.article}>
      <div className={classes.articleMeta}>
        <span className={classes.versionBadge}>
          {isLatest && <span className={classes.latestDot} aria-hidden />}v
          {nota.Versao}
          {isLatest ? " · atual" : ""}
        </span>
        {dataLabel && <time className={classes.date}>{dataLabel}</time>}
      </div>
      <h2 className={classes.articleTitle}>
        {nota.Titulo || `Versão ${nota.Versao}`}
      </h2>
      <div
        className={classes.content}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </article>
  );
}

function StateScreen({ classes, title, text, loading }) {
  return (
    <div className={classes.page}>
      <BrandHeader classes={classes} total={0} notas={[]} activeId={null} />
      <div className={classes.stateWrap}>
        {loading && <CircularProgress className={classes.spinner} />}
        <h2 className={classes.stateTitle}>{title}</h2>
        {text && <p className={classes.stateText}>{text}</p>}
      </div>
    </div>
  );
}

// Página é um livro único com TODAS as versões já publicadas — cada versão é uma seção
// ancorada por #v{Versao}. O push do app e qualquer link externo apontam direto pra essa
// âncora; o navegador rola até a seção certa sozinho ao abrir a URL.
export default function NotasAtualizacaoPublica() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [notas, setNotas] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    ensureAlexandriaFont();
    document.title = "Notas de atualização · konnect+";
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      try {
        const resultado = await fetchNotasPublicas();
        if (ativo) setNotas(resultado);
      } catch (error) {
        if (ativo) {
          setErro(
            error?.message ||
              "Não foi possível carregar as notas de atualização.",
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  // Só depois que as seções existem no DOM — se rolar antes de carregar, o id ainda não existe.
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const alvo = document.getElementById(window.location.hash.slice(1));
    if (!alvo) return;
    alvo.scrollIntoView({ block: "start", behavior: "smooth" });
    setActiveId(alvo.id);
  }, [loading, notas]);

  useEffect(() => {
    if (loading || !notas.length) return;

    const sections = notas
      .map((nota) => document.getElementById(anchorId(nota.Versao)))
      .filter(Boolean);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0.1, 0.35, 0.6],
      },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [loading, notas]);

  if (loading) {
    return (
      <StateScreen
        classes={classes}
        loading
        title="Carregando notas…"
        text="Buscando as versões publicadas do konnect+."
      />
    );
  }

  if (erro) {
    return (
      <StateScreen
        classes={classes}
        title="Não foi possível carregar"
        text={erro}
      />
    );
  }

  if (notas.length === 0) {
    return (
      <StateScreen
        classes={classes}
        title="Nenhuma versão publicada"
        text="Assim que uma nota de atualização for publicada, ela aparece aqui."
      />
    );
  }

  const year = new Date().getFullYear();

  return (
    <div className={classes.page}>
      <BrandHeader
        classes={classes}
        total={notas.length}
        notas={notas}
        activeId={activeId}
      />

      <div className={classes.layout}>
        <VersionNav
          classes={classes}
          notas={notas}
          activeId={activeId}
          variant="desktop"
        />

        <main className={classes.feed}>
          {notas.map((nota, indice) => (
            <ReleaseArticle
              key={nota.Versao}
              classes={classes}
              nota={nota}
              isLatest={indice === 0}
            />
          ))}
        </main>
      </div>

      <footer className={classes.footer}>
        © {year} Karsten · Trussardi — konnect+
      </footer>
    </div>
  );
}
